import { FC, Suspense, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { useMatch } from "react-router-dom";
import { graphql } from "relay-runtime";
import { usePreloadedAccountData } from "../services/accountData";
import { StrategyAccountFragment$key } from "./__generated__/StrategyAccountFragment.graphql";
import { StrategyQuery } from "./__generated__/StrategyQuery.graphql";
import { StrategyRootQuery } from "./__generated__/StrategyRootQuery.graphql";
import { BowPoolXyk } from "./components/BowPoolXyk";
import { StrategyContainer } from "./components/Common";
import { GhostVault } from "./components/GhostVault";
import { PerpsPool } from "./components/PerpsPool";
import { StakingPool } from "./components/StakingPoolPage";
import { ThorchainPool } from "./components/ThorchainPool";

const accountFragment = graphql`
  fragment StrategyAccountFragment on Account {
    ...BowPoolXykAccountFragment
    ...PerpsPoolAccountFragment
    ...ThorchainPoolAccountFragment
    ...StakingPoolPageAccountFragment
  }
`;

const rootQuery = graphql`
  query StrategyRootQuery {
    strategies(
      first: 100
      typenames: [
        "BowPoolXyk"
        "PerpsPool"
        "ThorchainPool"
        "StakingPool"
        "GhostVault"
      ]
    ) {
      edges {
        cursor
        node {
          __typename
          ... on BowPoolXyk {
            id
            config {
              x {
                metadata {
                  symbol
                }
              }
              y {
                metadata {
                  symbol
                }
              }
            }
          }
          ... on PerpsPool {
            id
            baseAsset {
              metadata {
                symbol
              }
            }
            quoteAsset {
              metadata {
                symbol
              }
            }
          }
          ... on StakingPool {
            id
            bondAsset {
              metadata {
                symbol
              }
            }
          }
          ... on ThorchainPool {
            id
            asset {
              asset
            }
          }
          ... on GhostVault {
            id
            asset {
              asset
            }
          }
        }
      }
    }
  }
`;

const query = graphql`
  query StrategyQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on BowPoolXyk {
        ...BowPoolXykFragment
      }
      ... on PerpsPool {
        ...PerpsPoolFragment
      }
      ... on ThorchainPool {
        ...ThorchainPoolFragment
      }
      ... on StakingPool {
        ...StakingPoolPageFragment
      }
      ... on GhostVault {
        ...GhostVaultFragment
      }
    }
  }
`;

export const Strategy: FC = () => {
  return (
    <Suspense fallback={<Fallback />}>
      <QueryProvider />
    </Suspense>
  );
};

const QueryProvider: FC = () => {
  const [q, loadQuery] = useQueryLoader<StrategyQuery>(query);
  const match = useMatch(`/strategies/:category/:type?/:assets`);
  const { strategies } = useLazyLoadQuery<StrategyRootQuery>(rootQuery, {});

  const node =
    match &&
    ((category, type, assets) => {
      switch (category) {
        case "amm":
          switch (type) {
            case "thorchain":
              return strategies.edges?.find(
                (a) =>
                  a?.node?.__typename === "ThorchainPool" &&
                  a.node.asset.asset === assets
              );
            case "xyk":
              if (!assets) return null;
              const [x, y] = assets.split("-");
              return strategies.edges?.find(
                (p) =>
                  p?.node?.__typename === "BowPoolXyk" &&
                  p?.node.config.x?.metadata.symbol === x &&
                  p?.node.config.y?.metadata.symbol === y
              );
          }
          break;
        case "derivatives":
          switch (type) {
            case "perps":
              if (!assets) {
                return null;
              }
              const [base, quote] = assets.split("-");
              return strategies.edges?.find(
                (p) =>
                  p?.node?.__typename === "PerpsPool" &&
                  p.node.baseAsset.metadata.symbol === base &&
                  p?.node.quoteAsset.metadata.symbol === quote
              );
          }
          break;
        case "staking":
          return strategies.edges?.find((p) => {
            return (
              p?.node?.__typename === "StakingPool" &&
              p?.node.bondAsset.metadata.symbol === assets
            );
          });
        case "lend":
          return strategies.edges?.find((p) => {
            return (
              p?.node?.__typename === "GhostVault" &&
              p?.node.asset.asset === assets
            );
          });
      }
    })(match.params.category, match.params.type, match.params.assets);
  const id = node?.node?.__typename !== "%other" ? node?.node?.id : undefined;
  useEffect(() => {
    if (!id) return;
    loadQuery({ id }, { fetchPolicy: "store-and-network" });
  }, [id]);

  return q ? <Content q={q} /> : <Fallback />;
};

const Content: FC<{
  q: PreloadedQuery<StrategyQuery>;
}> = ({ q }) => {
  const data = usePreloadedQuery(query, q);
  const { accountData } = usePreloadedAccountData();
  const account =
    useFragment<StrategyAccountFragment$key>(accountFragment, accountData) ||
    undefined;

  switch (data.node?.__typename) {
    case "BowPoolXyk":
      return <BowPoolXyk k={data.node} a={account} />;
    case "PerpsPool":
      return <PerpsPool k={data.node} a={account} />;
    case "ThorchainPool":
      return <ThorchainPool k={data.node} a={account} />;
    case "StakingPool":
      return <StakingPool k={data.node} a={account} />;
    case "GhostVault":
      return <GhostVault k={data.node} />;
  }
};

const Fallback: FC = () => {
  return (
    <StrategyContainer>
      <div className="flex ai-c wrap mt-2">
        <div className="skeleton h-6 w-12 w-sm-18 w-md-22 w-lg-45 br-6 mr-2" />

        <div className="lp big mr-3">
          <div className="icon-denom skeleton br-4" />
          <div className="icon-denom skeleton br-4" />
        </div>
        <div className="skeleton h-3.5 w-10 w-lg-26.5 br-4" />
      </div>
    </StrategyContainer>
  );
};
